import { RoutePlanFeedbackRequestDTO } from 'api/model';
import { useRoutePlanningSendFeedback } from 'api/route-planning/route-planning';
import Button from 'components/inputs/Button';
import Select from 'components/inputs/Select';
import TextArea from 'components/inputs/TextArea';
import Modal from 'components/Modal';
import { orange } from 'constants/Colors';
import {
  faCircleInfo,
  faImage,
  faPaperPlane,
  faPlus,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import styled, { css } from 'styled-components';
import StarRating from '../StarRating';
import { RoutePlanFeedbackSnapshot } from './feedback-helpers';
import {
  FeedbackScreenshot,
  imagesFromClipboard,
  isSupportedImage,
  MAX_SCREENSHOTS,
  prepareScreenshot,
} from './screenshot-helpers';

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  width: 560px;
  max-width: calc(100vw - 80px);
`;

const Explanation = styled.p`
  display: flex;
  gap: 10px;
  margin: 0;
  padding: 10px 12px;
  border-radius: 6px;
  background: ${({ theme }) => theme.colors.background.highlight};
  font-size: 0.9em;
  line-height: 1.45;
  color: rgb(60, 70, 85);

  svg {
    margin-top: 3px;
    color: rgb(30, 90, 160);
  }
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.span`
  font-size: 0.85em;
  font-weight: 600;
  color: rgb(50, 50, 50);
`;

const Optional = styled.span`
  font-weight: 400;
  color: rgb(140, 140, 140);
`;

const fullWidthInput = css`
  width: 100%;
  box-sizing: border-box;
  padding: 8px 10px;
  border-radius: 6px;
  border-color: rgb(200, 200, 200);

  &:focus {
    outline: none;
    border-color: ${orange};
    box-shadow: 0 0 0 3px rgb(204 102 51 / 18%);
  }
`;

const FullWidthSelect = styled(Select)`
  ${fullWidthInput}
`;

const FullWidthTextArea = styled(TextArea)`
  ${fullWidthInput}
  min-height: 120px;
  resize: vertical;
  line-height: 1.4;
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Chip = styled.button<{ selected: boolean }>`
  padding: 6px 12px;
  border: 1px solid
    ${({ selected }) => (selected ? orange : 'rgb(205, 205, 205)')};
  border-radius: 999px;
  background: ${({ selected }) =>
    selected ? 'rgb(204 102 51 / 10%)' : '#fff'};
  font: inherit;
  font-size: 0.85em;
  font-weight: ${({ selected }) => (selected ? 600 : 400)};
  color: ${({ selected }) =>
    selected ? 'rgb(160, 70, 30)' : 'rgb(70, 70, 70)'};
  cursor: pointer;
  transition:
    background 0.1s ease,
    border-color 0.1s ease;

  &:hover {
    border-color: ${orange};
  }
`;

const DropZone = styled.div<{ dragging: boolean; compact: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: ${({ compact }) => (compact ? '10px' : '18px 12px')};
  border: 2px dashed
    ${({ dragging }) => (dragging ? orange : 'rgb(200, 200, 200)')};
  border-radius: 8px;
  background: ${({ dragging }) =>
    dragging ? 'rgb(204 102 51 / 7%)' : 'rgb(250, 250, 250)'};
  transition:
    background 0.1s ease,
    border-color 0.1s ease;
`;

const DropPrompt = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  font-size: 0.9em;
  color: rgb(90, 90, 90);
  cursor: pointer;

  svg {
    font-size: 22px;
    color: rgb(160, 160, 160);
  }

  strong {
    color: ${orange};
  }

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;

const Screenshots = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Thumb = styled.div`
  position: relative;
  width: 100px;
  height: 68px;
  border: 1px solid rgb(210, 210, 210);
  border-radius: 6px;
  overflow: hidden;
  background: #fff;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const RemoveThumb = styled.button`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgb(0 0 0 / 60%);
  color: #fff;
  font-size: 11px;
  cursor: pointer;

  &:hover {
    background: rgb(0 0 0 / 80%);
  }
`;

const AddTile = styled.button`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  width: 100px;
  height: 68px;
  border: 1px dashed rgb(170, 170, 170);
  border-radius: 6px;
  background: #fff;
  font: inherit;
  font-size: 0.8em;
  color: rgb(90, 90, 90);
  cursor: pointer;

  &:hover {
    border-color: ${orange};
    color: ${orange};
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;

const Hint = styled.span`
  font-size: 0.8em;
  color: rgb(130, 130, 130);
`;

const Kbd = styled.kbd`
  padding: 0 4px;
  border: 1px solid rgb(200, 200, 200);
  border-bottom-width: 2px;
  border-radius: 3px;
  background: #fff;
  font-family: inherit;
  font-size: 0.95em;
`;

const Details = styled.details`
  font-size: 0.8em;
  color: rgb(110, 110, 110);

  summary {
    cursor: pointer;
    user-select: none;
  }

  ul {
    margin: 6px 0 0;
    padding-left: 18px;
  }
`;

const Footer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  margin: 0 -20px -20px;
  padding: 14px 20px;
  border-top: 1px solid rgb(230, 230, 230);
  background: rgb(250, 250, 250);
`;

const FooterHint = styled.span`
  margin-right: auto;
  font-size: 0.8em;
  color: rgb(140, 140, 140);
`;

const CancelButton = styled.button`
  padding: 10px 16px;
  border: none;
  border-radius: 3px;
  background: none;
  font: inherit;
  color: rgb(80, 80, 80);
  cursor: pointer;

  &:hover {
    background: rgb(235, 235, 235);
  }
`;

const PackageBanner = styled.div`
  padding: 10px 12px;
  border: 1px solid rgb(225, 225, 225);
  border-radius: 6px;
  font-size: 0.9em;
  font-weight: 600;
  color: rgb(50, 50, 50);
`;

export const RATING_CATEGORY = 'Betyg på ruttförslag';

const CATEGORIES = [
  'Fel eller dåligt förslag',
  'Saknad information',
  'Bugg',
  'Idé / önskemål',
  'Övrigt',
];

interface Props {
  snapshot: RoutePlanFeedbackSnapshot;
  /** Satt ⇒ rutan betygsätter just det här paketet: 1–5 plus obligatorisk motivering. */
  ratingPackageIndex?: number;
  onClose(): void;
  /** Anropas efter lyckad sändning, med betyget om det var ett. */
  onSent?(rating?: number): void;
}

/**
 * Feedback från trafikledaren, med vyns tillstånd bifogat. Ögonblicksbilden togs när ikonen
 * klickades, så det som skickas är läget planeraren reagerade på.
 */
const FeedbackModal: React.FC<Props> = ({
  snapshot,
  ratingPackageIndex,
  onClose,
  onSent,
}) => {
  const isRating = ratingPackageIndex !== undefined;
  const [rating, setRating] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [category, setCategory] = useState(CATEGORIES[0]);
  const [packageIndex, setPackageIndex] = useState<string>(
    isRating ? String(ratingPackageIndex) : ''
  );
  const [screenshots, setScreenshots] = useState<FeedbackScreenshot[]>([]);
  const [isReadingImages, setIsReadingImages] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const dragDepth = useRef(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const sendMutation = useRoutePlanningSendFeedback();

  const addImages = useCallback(
    async (files: File[]) => {
      const images = files.filter(isSupportedImage);
      if (images.length < files.length) {
        toast.error('Bara PNG, JPEG, GIF och WebP kan bifogas.');
      }
      const room = MAX_SCREENSHOTS - screenshots.length;
      if (images.length > room) {
        toast.error(`Högst ${MAX_SCREENSHOTS} skärmdumpar kan bifogas.`);
      }
      const toAdd = images.slice(0, Math.max(0, room));
      if (toAdd.length === 0) return;

      setIsReadingImages(true);
      try {
        const prepared = await Promise.all(toAdd.map(prepareScreenshot));
        setScreenshots((prev) =>
          [...prev, ...prepared].slice(0, MAX_SCREENSHOTS)
        );
      } catch {
        toast.error('En bild gick inte att läsa.');
      } finally {
        setIsReadingImages(false);
      }
    },
    [screenshots.length]
  );

  // Klistra in var som helst i rutan — det vanliga flödet är Win+Shift+S / Cmd+Ctrl+Shift+4 och
  // sedan Ctrl+V. Text som klistras in i textrutan lämnas i fred.
  const onPaste = (e: React.ClipboardEvent) => {
    const images = imagesFromClipboard(e.clipboardData);
    if (images.length === 0) return;
    e.preventDefault();
    addImages(images);
  };

  // Dra och släpp: hela rutan tar emot filer, så att en bild som släpps lite utanför bildytan inte
  // öppnas av webbläsaren istället. Djupräknaren håller markeringen stilla när musen passerar barn.
  const isFileDrag = (e: React.DragEvent) =>
    Array.from(e.dataTransfer.types).includes('Files');

  const onDragEnter = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current += 1;
    setIsDragging(true);
  };

  const onDragOver = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect =
      screenshots.length < MAX_SCREENSHOTS ? 'copy' : 'none';
  };

  const onDragLeave = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) setIsDragging(false);
  };

  const onDrop = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    dragDepth.current = 0;
    setIsDragging(false);
    addImages(Array.from(e.dataTransfer.files));
  };

  const { proposal } = snapshot.state;
  const packages = snapshot.packageOverview;

  const ratedPackage = isRating
    ? packages.find((p) => p.packageIndex === ratingPackageIndex)
    : undefined;
  const ratedPackageLabel = ratedPackage
    ? `Paket #${ratedPackage.packageIndex} · ${ratedPackage.selectedFieldTester ?? 'ingen FT'} · ${ratedPackage.legs.join(' | ')}`
    : `Paket #${ratingPackageIndex}`;

  const canSend =
    message.trim().length > 0 &&
    !(isRating && rating === null) &&
    !sendMutation.isLoading &&
    !isReadingImages;

  const send = async () => {
    if (!canSend) return;
    const related = packageIndex === '' ? undefined : Number(packageIndex);
    const request: RoutePlanFeedbackRequestDTO = {
      message: message.trim(),
      category: isRating ? RATING_CATEGORY : category,
      relatedPackageIndex: related,
      rating: isRating && rating !== null ? rating : undefined,
      proposalId: proposal?.proposalId,
      planDate: snapshot.state.filters.date,
      filterSummary: snapshot.filterSummary,
      stateSummary:
        related === undefined
          ? snapshot.stateSummary
          : [
              ...snapshot.stateSummary,
              ...packages
                .filter((p) => p.packageIndex === related)
                .map(
                  (p) =>
                    `Paket #${p.packageIndex}: ${p.legs.join(' | ')} · FT ${p.selectedFieldTester ?? '—'} · ${p.included ? 'markerat' : 'ej markerat'} · ${p.applyState}`
                ),
            ],
      pageUrl: snapshot.page.url,
      userAgent: snapshot.page.userAgent,
      // Betyget och det betygsatta paketet ligger överst i bilagan, så man slipper leta upp
      // paketet i hela förslaget.
      stateJson: JSON.stringify(
        isRating
          ? {
              rating: {
                packageIndex: ratingPackageIndex,
                rating,
                reason: message.trim(),
                overview: ratedPackage,
                package: proposal?.packages.find(
                  (p) => p.packageIndex === ratingPackageIndex
                ),
                selection: snapshot.state.selections[ratingPackageIndex],
              },
              ...snapshot,
            }
          : snapshot,
        null,
        2
      ),
      screenshots: screenshots.map((shot) => ({
        fileName: shot.fileName,
        contentType: shot.contentType,
        base64Data: shot.base64Data,
      })),
    };

    try {
      await sendMutation.mutateAsync({ data: request });
    } catch {
      toast.error('Kunde inte skicka feedbacken — försök igen.');
      return;
    }
    toast.success(
      isRating ? 'Tack för betyget!' : 'Tack! Feedbacken är skickad.'
    );
    onSent?.(isRating && rating !== null ? rating : undefined);
    onClose();
  };

  const canAddMore = screenshots.length < MAX_SCREENSHOTS;
  const openFilePicker = () => fileInputRef.current?.click();

  return (
    <Modal
      closeOnOutsideClick={false}
      onClose={onClose}
      title={isRating ? 'Betygsätt ruttförslag' : 'Feedback på ruttplaneringen'}
    >
      <Body
        onDragEnter={onDragEnter}
        onDragLeave={onDragLeave}
        onDragOver={onDragOver}
        onDrop={onDrop}
        onPaste={onPaste}
      >
        <Explanation>
          <FontAwesomeIcon icon={faCircleInfo} />
          <span>
            {isRating
              ? 'Hur bra är det här ruttförslaget? Motivera betyget — det är motiveringen som gör det möjligt att förbättra planeringen.'
              : 'Berätta vad som var fel, saknades eller kunde vara bättre.'}{' '}
            Ruttplaneringens läge just nu — filter, förslaget, dina markeringar
            och vad som verkställts — skickas med automatiskt.
          </span>
        </Explanation>

        {isRating && (
          <>
            <PackageBanner>{ratedPackageLabel}</PackageBanner>
            <Field as="div">
              <FieldLabel>Betyg</FieldLabel>
              <StarRating value={rating ?? undefined} onChange={setRating} />
            </Field>
          </>
        )}

        {!isRating && (
          <Field as="div">
            <FieldLabel>Vad gäller det?</FieldLabel>
            <Chips role="radiogroup">
              {CATEGORIES.map((c) => (
                <Chip
                  aria-checked={category === c}
                  key={c}
                  onClick={() => setCategory(c)}
                  role="radio"
                  selected={category === c}
                  type="button"
                >
                  {c}
                </Chip>
              ))}
            </Chips>
          </Field>
        )}

        {!isRating && packages.length > 0 && (
          <Field>
            <FieldLabel>
              Gäller paket <Optional>(valfritt)</Optional>
            </FieldLabel>
            <FullWidthSelect
              onChange={(e) => setPackageIndex(e.target.value)}
              value={packageIndex}
            >
              <option value="">Hela förslaget</option>
              {packages.map((p) => (
                <option key={p.packageIndex} value={p.packageIndex}>
                  #{p.packageIndex} · {p.selectedFieldTester ?? 'ingen FT'} ·{' '}
                  {p.legs[0] ?? ''}
                  {p.legs.length > 1 ? ` (+${p.legs.length - 1})` : ''}
                </option>
              ))}
            </FullWidthSelect>
          </Field>
        )}

        <Field>
          <FieldLabel>
            {isRating ? (
              <>
                Varför? <Optional>(obligatoriskt)</Optional>
              </>
            ) : (
              'Feedback'
            )}
          </FieldLabel>
          <FullWidthTextArea
            autoFocus={!isRating}
            onChange={(e) => setMessage(e.currentTarget.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                send();
              }
            }}
            placeholder={
              isRating
                ? 't.ex. bra ordning på benen, men Kalle bor närmare första upphämtningen'
                : 't.ex. paket 3 borde köras av Kalle, han bor närmare första upphämtningen'
            }
            value={message}
          />
        </Field>

        <Field as="div">
          <FieldLabel>
            Skärmdumpar{' '}
            <Optional>
              (valfritt
              {screenshots.length > 0
                ? ` · ${screenshots.length}/${MAX_SCREENSHOTS}`
                : ''}
              )
            </Optional>
          </FieldLabel>
          <DropZone
            compact={screenshots.length > 0}
            dragging={isDragging && canAddMore}
          >
            {screenshots.length === 0 ? (
              <DropPrompt
                disabled={isReadingImages}
                onClick={openFilePicker}
                type="button"
              >
                <FontAwesomeIcon icon={faImage} />
                {isReadingImages ? (
                  'Läser bild…'
                ) : isDragging ? (
                  <strong>Släpp bilden här</strong>
                ) : (
                  <span>
                    Dra hit en bild, klistra in med <Kbd>Ctrl</Kbd>+<Kbd>V</Kbd>{' '}
                    eller <strong>välj fil</strong>
                  </span>
                )}
              </DropPrompt>
            ) : (
              <Screenshots>
                {screenshots.map((shot, index) => (
                  <Thumb key={shot.id}>
                    <img
                      alt={`Skärmdump ${index + 1}`}
                      src={shot.previewUrl}
                      title={shot.fileName}
                    />
                    <RemoveThumb
                      aria-label={`Ta bort skärmdump ${index + 1}`}
                      onClick={() =>
                        setScreenshots((prev) =>
                          prev.filter((s) => s.id !== shot.id)
                        )
                      }
                      title="Ta bort"
                      type="button"
                    >
                      <FontAwesomeIcon icon={faXmark} />
                    </RemoveThumb>
                  </Thumb>
                ))}
                {canAddMore && (
                  <AddTile
                    disabled={isReadingImages}
                    onClick={openFilePicker}
                    type="button"
                  >
                    <FontAwesomeIcon icon={faPlus} />
                    {isReadingImages ? 'Läser…' : 'Lägg till'}
                  </AddTile>
                )}
              </Screenshots>
            )}
            <input
              accept="image/png,image/jpeg,image/gif,image/webp"
              hidden
              multiple
              onChange={(e) => {
                addImages(Array.from(e.currentTarget.files ?? []));
                e.currentTarget.value = '';
              }}
              ref={fileInputRef}
              type="file"
            />
          </DropZone>
          <Hint>
            Ta en skärmdump med <Kbd>Win</Kbd>+<Kbd>Shift</Kbd>+<Kbd>S</Kbd>{' '}
            (Mac: <Kbd>Cmd</Kbd>+<Kbd>Ctrl</Kbd>+<Kbd>Shift</Kbd>+<Kbd>4</Kbd>)
            och klistra in den här. Högst {MAX_SCREENSHOTS} bilder.
          </Hint>
        </Field>

        <Details>
          <summary>Det här skickas med</summary>
          <ul>
            <li>{snapshot.filterSummary}</li>
            {snapshot.stateSummary.map((line) => (
              <li key={line}>{line}</li>
            ))}
            <li>Hela förslaget och vyns tillstånd som JSON-bilaga</li>
            {screenshots.length > 0 && (
              <li>
                {screenshots.length} skärmdump
                {screenshots.length === 1 ? '' : 'ar'}
              </li>
            )}
          </ul>
        </Details>

        <Footer>
          <FooterHint>
            <Kbd>Ctrl</Kbd>+<Kbd>Enter</Kbd> skickar
          </FooterHint>
          <CancelButton onClick={onClose} type="button">
            Avbryt
          </CancelButton>
          <Button
            disabled={!canSend}
            icon={<FontAwesomeIcon icon={faPaperPlane} />}
            loading={sendMutation.isLoading}
            onClick={send}
            type="button"
          >
            {isRating ? 'Skicka betyg' : 'Skicka feedback'}
          </Button>
        </Footer>
      </Body>
    </Modal>
  );
};

export default FeedbackModal;
